// Bridge di stato del funnel /verifica-copertura. Replica i due "store" del prototipo di
// design (sh_coverage + sh_cart), tipizzato e SSG/idratazione-safe: ogni accesso è guardato
// da `typeof window` e da try/catch (Safari private / quota). Mirror cookie + localStorage,
// così la verifica di copertura e il pacchetto configurato sopravvivono al cambio pagina.
//
// IMPORTANTE: mai leggere questi store durante il render/prerender — solo dentro useEffect.

import type { Comune, Via, Civico, CoverageResult, CoverageLinea } from '@/lib/api'

export type CheckoutIntent = 'activate' | 'quote' | 'callback'
export type CheckoutSegment = 'casa' | 'business'
export type CheckoutTech = 'ftth' | 'fttc' | 'fwa'

export interface SavedComune { egonId: string; nome: string; provincia?: string; cap?: string }
export interface SavedVia { egonId: string; nome: string; frazione?: string; cap?: string; provincia?: string }
export interface SavedCivico { egonId: string; numero: string }

export interface SavedCoverage {
  covered: boolean
  avail: Record<CheckoutTech, boolean>
  recommended: CheckoutTech | null
  address: string
  // Velocità reale attivabile e nota distanza (cabinet/BTS) della tecnologia
  // consigliata: mostrate nella modale esito. Assenti nei cookie pre-esistenti.
  down?: string
  up?: string
  dist?: string
  comune?: SavedComune
  via?: SavedVia
  civico?: SavedCivico
  ts: number
}

export interface SavedCartSim { id: string; qty: number; name: string; data?: string; net?: string; price?: number }

export interface SavedCart {
  segment: CheckoutSegment
  intent: CheckoutIntent
  techKey?: CheckoutTech
  techName?: string
  down?: string                 // velocità reale attivabile (es. "1 Gbps")
  rete?: string                 // rete/fornitore reale (es. "Spadhausen", "FiberCop")
  durataMesi?: number           // durata del vincolo scelto (se vincolo)
  vincolo?: boolean             // true = con vincolo, false = nessun vincolo
  speedTier?: 'base' | 'up'     // FTTH: profilo di serie o upgrade a pagamento (10 Gbit)
  costoAttivazione?: number     // costo attivazione linea in base alla durata
  monthly: number
  sims: SavedCartSim[]
  voip?: { id: string; name: string; price?: number } | null
  ip?: { id: string; name: string; price?: number } | null
  centralino?: { id: string; name: string; price?: number } | null
  backup?: { mode: string; name: string; price?: number; subnet?: string } | null
  assistenza?: { id: string; name: string; price?: number } | null
  // Casa: scelta del router — modem libero (price 0) o comodato con Assistenza Standard.
  router?: { id: string; name: string; price?: number } | null
  simDiscount: number
  oneTimeSim: number
  oneTimeTotal?: number
  fonte?: string
  ts: number
}

const COVERAGE_KEY = 'sh_coverage'
const CART_KEY = 'sh_cart'
const MAX_AGE_MS = 1000 * 60 * 60 * 6 // 6h: oltre, la verifica si considera stantia

const isBrowser = () => typeof window !== 'undefined'

function writeStore(key: string, value: unknown): void {
  if (!isBrowser()) return
  try {
    const payload = encodeURIComponent(JSON.stringify(value))
    document.cookie = `${key}=${payload}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`
    window.localStorage.setItem(key, payload)
  } catch {
    /* private mode / quota: best-effort */
  }
}

function readStore<T>(key: string): T | null {
  if (!isBrowser()) return null
  try {
    const m = document.cookie.match(new RegExp('(?:^|; )' + key + '=([^;]*)'))
    const raw = m ? m[1] : window.localStorage.getItem(key)
    if (!raw) return null
    return JSON.parse(decodeURIComponent(raw)) as T
  } catch {
    return null
  }
}

function clearStore(key: string): void {
  if (!isBrowser()) return
  try {
    document.cookie = `${key}=; path=/; max-age=0`
    window.localStorage.removeItem(key)
  } catch {
    /* noop */
  }
}

// ---- Coverage ----
export function writeCoverage(coverage: Omit<SavedCoverage, 'ts'>): void {
  writeStore(COVERAGE_KEY, { ...coverage, ts: Date.now() })
}
export function readCoverage(): SavedCoverage | null {
  const c = readStore<SavedCoverage>(COVERAGE_KEY)
  return c && c.address ? c : null
}
export function clearCoverage(): void {
  clearStore(COVERAGE_KEY)
}

// ---- Cart (pacchetto configurato) ----
export function writeCart(cart: Omit<SavedCart, 'ts'>): void {
  writeStore(CART_KEY, { ...cart, ts: Date.now() })
}
export function readCart(): SavedCart | null {
  const c = readStore<SavedCart>(CART_KEY)
  return c && c.segment ? c : null
}
export function clearCart(): void {
  clearStore(CART_KEY)
}

export function isFresh(ts: number | undefined | null, maxAgeMs = MAX_AGE_MS): boolean {
  return !!ts && Date.now() - ts < maxAgeMs
}

export function fmtAge(ts: number | undefined | null): string {
  if (!ts) return ''
  const mins = Math.round((Date.now() - ts) / 60000)
  if (mins < 1) return 'pochi istanti fa'
  if (mins < 60) return `${mins} min fa`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs} ${hrs === 1 ? 'ora' : 'ore'} fa`
  const days = Math.round(hrs / 24)
  return `${days} ${days === 1 ? 'giorno' : 'giorni'} fa`
}

function fmtSpeed(mbps: number): string {
  if (mbps >= 1000) {
    const g = mbps / 1000
    return `${Number.isInteger(g) ? g : g.toFixed(1).replace('.', ',')} Gbps`
  }
  return `${mbps} Mbps`
}

function fmtMetri(m: number): string {
  if (m >= 1000) {
    const km = m / 1000
    return `${Number.isInteger(km) ? km : km.toFixed(1).replace('.', ',')} km`
  }
  return `${m} m`
}

// Nota distanza per la modale esito: armadio stradale per FTTC/VULA; per FWA il
// backend valorizza cabinetDistance con la distanza dalla BTS solo su rete Spadhausen.
export function lineaDistanceNote(linea: CoverageLinea): string | null {
  if (linea.cabinetDistance == null) return null
  const tech = linea.tecnologia.toUpperCase()
  if (tech === 'FTTC' || tech === 'VULA') return `Distanza dal cabinet: ~${fmtMetri(linea.cabinetDistance)}`
  if (tech === 'FWA' && /spadhausen/i.test(linea.fornitore)) return `Distanza stimata dalla BTS: ~${fmtMetri(linea.cabinetDistance)}`
  return null
}

// Costruisce un SavedCoverage dall'esito reale dell'API + indirizzo selezionato.
// Stessa logica di derivazione tech di HeroCoverage.buildVM.
export function coverageFromResult(
  result: CoverageResult,
  comune: Comune,
  via: Via,
  civico: Civico,
): Omit<SavedCoverage, 'ts'> {
  const attivabile = (tech: string) =>
    result.linee.some((l) => l.tecnologia.toUpperCase() === tech && l.stato === 'Attivabile')
  const avail: Record<CheckoutTech, boolean> = {
    ftth: attivabile('FTTH'),
    fttc: attivabile('FTTC') || attivabile('VULA'),
    fwa: attivabile('FWA'),
  }
  const recommended: CheckoutTech | null = avail.ftth ? 'ftth' : avail.fttc ? 'fttc' : avail.fwa ? 'fwa' : null
  const covered = result.coperto || recommended !== null
  // Prima linea attivabile della tech consigliata (ordine payload = migliore in cima).
  const techNames: Record<CheckoutTech, string[]> = { ftth: ['FTTH'], fttc: ['FTTC', 'VULA'], fwa: ['FWA'] }
  const linea = recommended
    ? result.linee.find((l) => techNames[recommended].includes(l.tecnologia.toUpperCase()) && l.stato === 'Attivabile')
    : undefined
  return {
    covered,
    avail,
    recommended,
    address: `${via.nome} ${civico.numero}, ${comune.nome}`,
    down: linea && fmtSpeed(linea.velocitaDownloadMbps),
    up: linea && fmtSpeed(linea.velocitaUploadMbps),
    dist: linea ? lineaDistanceNote(linea) ?? undefined : undefined,
    comune: { egonId: comune.egonId, nome: comune.nome, provincia: comune.provincia, cap: comune.cap },
    via: { egonId: via.egonId, nome: via.nome, frazione: via.frazione, cap: via.cap, provincia: via.provincia },
    civico: { egonId: civico.egonId, numero: civico.numero },
  }
}
